// src/services/axiosInstance.js
import axios from 'axios';

// Create an Axios instance
const api = axios.create({
    baseURL: process.env.REACT_APP_API_URL, // Update with your backend URL
    withCredentials: true, // Include cookies in requests
});

// Set default headers (if needed)
api.defaults.headers.common['Content-Type'] = 'application/json';

// Optionally set an Authorization header if you have a token
// This can be updated dynamically before each request
// Request Interceptor
/** No need to add Authorization header as httpOnly cookie is used.
api.interceptors.request.use(config => {
    const token = localStorage.getItem('accessToken'); // Adjust this based on your token storage
    if (token) {
        config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
}, error => {
    return Promise.reject(error);
});
*/
api.interceptors.request.use((request) => {
    //console.log('Starting Request', request);
    return request;
});

// Handle errors centrally
/** Centralized error handling
const isTokenExpired = () => {
    const expiryTime = localStorage.getItem('tokenExpiry');
    return Date.now() >= expiryTime;
};
*/ 

// Logging out flag
let loggingOut = false; // Flag to track logout state

/**
 * If you turn on maintenance while a user is already using the app, their next click will trigger an API call that returns a 503. You should catch this and force a refresh or redirect.
 * window.location.reload() is often more robust for a Single Page Application (SPA) like React.
 * When the 503 error hits:
 * 1. Hard Reset: window.location.reload() wipes the browser's RAM and re-fetches the entire app.
 * 2. Environmental Trigger: Upon reload, the logic in Step 3 (your App.js gate) immediately detects the REACT_APP_MAINTENANCE_MODE flag.
 * 3. Clean Break: The user is presented with only the maintenance page. No background processes, no old data, and no accidental navigation back to previous pages. (Avoid Partial App Load and Security/Data Leak: : If a user was looking at sensitive data when you toggled maintenance mode, that data might stay in the React state/memory until they manually refresh.)
 */
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && error.response.status === 503) {
      // If the backend says maintenance, reload the page
      // This will trigger the logic in Step 3 and show the MaintenancePage
      window.location.reload();
    }
    return Promise.reject(error);
  }
);

// Function to set up interceptors with token refresh logic
// Response Interceptor
const setupInterceptors = (handleTokenRefresh) => {
    let isRefreshing = false; // To track if a refresh is in progress
    let subscribers = []; // To hold requests while refreshing

    const onRefreshed = () => {
        subscribers.forEach((callback) => callback()); // Call all stored callbacks
        subscribers = []; // Clear the queue
    };

    const addSubscriber = (callback) => {
        subscribers.push(callback); // Add callback to the queue
    };

    // Request Interceptor
    api.interceptors.request.use((request) => {
        // Prevent requests if logging out
        if (loggingOut) {
            return Promise.reject(new axios.Cancel('Request canceled due to logout.'));
        }
        return request;
    });


    api.interceptors.response.use(
        response => response,
        async error => {
            const originalRequest = error.config;
            if (error.response) {
                //console.log('axiosInstance.js', error.response.status, error.config.url, originalRequest._retry);
                
                // Handle 401 Unauthorized
                if (error.response.status === 401 && error.response.data.message === 'No token provided') {
                    if (!loggingOut) { // Only redirect if not already logging out
                        console.error(`No token provided for loggingOut: ${loggingOut}`);
                        loggingOut = true; // Set logout flag
                        window.location.href = '/logout'; // Redirect to logout page
                    }
                    return Promise.reject(error); // Ensure the chain stops
                }

                // Handle 403 Forbidden
                if (error.response.status === 403 && error.response.data.message === 'Invalid token') {
                    if (!originalRequest._retry){
                        if (isRefreshing) {
                            // If a refresh is in progress, wait and retry
                            return new Promise((resolve) => {
                                addSubscriber(() => {
                                    resolve(api(originalRequest)); // Retry the original request
                                });
                            });
                        }

                        //console.log('Attempting to refresh token...');
                        originalRequest._retry = true; // Prevent infinite retry
                        isRefreshing = true; // Set refreshing flag

                        try {
                            await handleTokenRefresh(); // Attempt to refresh the token
                            onRefreshed(); // Notify all subscribers
                            return api(originalRequest); // Retry the original request
                        } catch (refreshError) {
                           // console.log('axiosInstance.js', 'Token refresh failed', refreshError);
                           // console.error('Token refresh failed:', refreshError);
                            // Handle the failure (e.g., redirect to login)
                            console.error('Token refresh failed:', refreshError);
                            loggingOut = true; // Set logout flag
                            window.location.href = '/logout'; // Redirect to logout page
                            // Optionally alert the user
                            alert('Session expired. You have been logged out.');
                            return Promise.reject(refreshError); // <--- to ensure the chain stops:
                        } finally {
                            isRefreshing = false; // Reset refreshing flag
                        }
                    }
                }
                else{
                    console.error('Error response:', error.response.data);
                    //delete access token from local storage
            }

            } else if (error.request) {
                console.error('No response received for the request:', error.request);
            } else {
                console.error('Error Message:', error.message);
            }
            return Promise.reject(error);
        }
    );
};

export { api, setupInterceptors }; // Export both the API instance and setup function